import { ReactElement } from 'react';
import storeConnector from '@store/storeConnector';
import useGetInternalWidgetConfig from '@src/hooks/api/queries/useGetInternalWidgetConfig';
import { WidgetTypeDeviceList } from '@src/types/Widget';
import useSubscription from '@src/hooks/useSubscription';
import { SiteMeta } from '@src/types/SiteMeta';
import Resistor from '@src/sld/components/Resistor/Resistor';
import { Evelyn_Device_Points_Config } from './devicePointsConfig';
import { DataPointWithValue } from '@src/types/DataPoint';
import { getDataPointCollectionByPointNames } from '@src/sld/utils';
import Header from './components/Header';
import Switch from '@src/sld/components/Switch/Switch';
import VerticalLine from '@src/sld/components/Line/VerticalLine';
import BreakerBasic from '../components/Breaker/BreakerBasic';
import HorizontalLine from '@src/sld/components/Line/HorizontalLine';
import MultiSwitchConnector from '@src/sld/components/MultiSwitchConnector/MultiSwitchConnector';
import BreakerStack from './components/BreakerStack';
import ArrowDown from '../components/ArrowDown/ArrowDown';
import DeviceDataPointsSection from './components/DeviceDataPointsSection';

type EvelynSLDProps = {
  siteMeta: SiteMeta;
};

function Evelyn_Diagram(props: EvelynSLDProps): ReactElement {
  const { siteMeta } = props;

  type EngineeringShapeConfig = {
    shape: string;
    internalId: string;
    dataSourceDeviceType: string;
    label: string;
    dataSourceDeviceEngineeringId: string;
    points: {
      label: string;
      pointName: string;
      isStatusBinaryPoint: boolean;
    }[];
    dataPoints: DataPointWithValue[] | [];
  };

  const L1_Switch_Config: EngineeringShapeConfig | undefined =
    Evelyn_Device_Points_Config.L1_SWITCH;
  const L1_Breaker_Config: EngineeringShapeConfig | undefined =
    Evelyn_Device_Points_Config.L1_BREAKER;
  const F1_Breaker_Config: EngineeringShapeConfig | undefined =
    Evelyn_Device_Points_Config.F1_BREAKER;
  const F2_Breaker_Config: EngineeringShapeConfig | undefined =
    Evelyn_Device_Points_Config.F2_BREAKER;
  const F3_Breaker_Config: EngineeringShapeConfig | undefined =
    Evelyn_Device_Points_Config.F2_BREAKER;
  const F4_Breaker_Config: EngineeringShapeConfig | undefined =
    Evelyn_Device_Points_Config.F4_BREAKER;
  const F5_Breaker_Config: EngineeringShapeConfig | undefined =
    Evelyn_Device_Points_Config.F5_BREAKER;
  const Auxiliary_Feed_Config: EngineeringShapeConfig | undefined =
    Evelyn_Device_Points_Config.AUXILIARY_FEED;

  const { data: rtacWidgetConfig } =
    useGetInternalWidgetConfig<WidgetTypeDeviceList>(
      'evelyn_sld_rtac',
      {
        sourceDeviceId: siteMeta.siteId,
      },
      { enabled: true },
    );

  const sldDataPointsWithValues = useSubscription(
    rtacWidgetConfig?.dataPoints || [],
  );

  const getDataPointsForThisDevice = (
    dataSourceDeviceEngineeringId: string,
  ): DataPointWithValue[] => {
    return sldDataPointsWithValues.filter(
      (point) => point.sourceDeviceId === dataSourceDeviceEngineeringId,
    );
  };

  if (sldDataPointsWithValues.length > 0) {
    L1_Switch_Config.dataPoints = getDataPointCollectionByPointNames(
      getDataPointsForThisDevice(
        L1_Switch_Config.dataSourceDeviceEngineeringId,
      ),
      L1_Switch_Config.points.map((point) => point.pointName.toLowerCase()) ||
        [],
    );

    L1_Breaker_Config.dataPoints = getDataPointCollectionByPointNames(
      getDataPointsForThisDevice(
        L1_Breaker_Config.dataSourceDeviceEngineeringId,
      ),
      L1_Breaker_Config.points.map((point) => point.pointName.toLowerCase()) ||
        [],
    );

    F1_Breaker_Config.dataPoints = getDataPointCollectionByPointNames(
      getDataPointsForThisDevice(
        F1_Breaker_Config.dataSourceDeviceEngineeringId,
      ),
      F1_Breaker_Config.points.map((point) => point.pointName.toLowerCase()) ||
        [],
    );

    F2_Breaker_Config.dataPoints = getDataPointCollectionByPointNames(
      getDataPointsForThisDevice(
        F2_Breaker_Config.dataSourceDeviceEngineeringId,
      ),
      F2_Breaker_Config.points.map((point) => point.pointName.toLowerCase()) ||
        [],
    );

    F3_Breaker_Config.dataPoints = getDataPointCollectionByPointNames(
      getDataPointsForThisDevice(
        F3_Breaker_Config.dataSourceDeviceEngineeringId,
      ),
      F3_Breaker_Config.points.map((point) => point.pointName.toLowerCase()) ||
        [],
    );

    F4_Breaker_Config.dataPoints = getDataPointCollectionByPointNames(
      getDataPointsForThisDevice(
        F4_Breaker_Config.dataSourceDeviceEngineeringId,
      ),
      F4_Breaker_Config.points.map((point) => point.pointName.toLowerCase()) ||
        [],
    );

    F5_Breaker_Config.dataPoints = getDataPointCollectionByPointNames(
      getDataPointsForThisDevice(
        F5_Breaker_Config.dataSourceDeviceEngineeringId,
      ),
      F5_Breaker_Config.points.map((point) => point.pointName.toLowerCase()) ||
        [],
    );

    Auxiliary_Feed_Config.dataPoints = getDataPointCollectionByPointNames(
      getDataPointsForThisDevice(
        Auxiliary_Feed_Config.dataSourceDeviceEngineeringId,
      ),
      Auxiliary_Feed_Config.points.map((point) =>
        point.pointName.toLowerCase(),
      ) || [],
    );
  }

  const getBreakerStatus = (
    sourceDeviceId: string,
    breakerStatusPointName: string,
    dataPoints: DataPointWithValue[] | undefined,
  ): boolean => {
    if (dataPoints) {
      const breakerDataPoints: DataPointWithValue[] = dataPoints.filter(
        (point) =>
          point.pointName === breakerStatusPointName &&
          point.sourceDeviceId === sourceDeviceId,
      );

      return breakerDataPoints.length > 0 && breakerDataPoints[0].value === 1;
    }

    return false;
  };

  const getSwitchStatus = (
    engineeringShapeConfig: EngineeringShapeConfig,
  ): boolean => {
    if (engineeringShapeConfig.dataPoints) {
      const statusPointName: string =
        engineeringShapeConfig.points.find((point) => point.isStatusBinaryPoint)
          ?.pointName || '';

      const switchDataPoints: DataPointWithValue[] =
        engineeringShapeConfig.dataPoints.filter(
          (dataPoint) => dataPoint.pointName === statusPointName,
        );

      return switchDataPoints.length > 0 && switchDataPoints[0].value === 1;
    }

    return false;
  };

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      xlinkHref='http://www.w3.org/1999/xlink'
      viewBox='200 40 1200 700'
      xmlSpace='preserve'
      colorInterpolationFilters='sRGB'
      className='st10'
      style={{ fill: 'none', stroke: 'currentColor', overflowX: 'auto' }}
    >
      <title>Evelyn SLD</title>
      <Header />

      {/* top switch */}
      <g transform='translate(743 145)'>
        <Switch
          isClosed={getSwitchStatus(L1_Switch_Config)}
          translateXCoordinate={0}
          translateYCoordinate={0}
          label='89-L1'
          stroke={getSwitchStatus(L1_Switch_Config) ? 'red' : 'green'}
        />

        <VerticalLine
          translateXCoordinate={0}
          translateYCoordinate={11}
          length={30}
          color='white'
          width={0.5}
        />
      </g>

      {/* top breaker */}
      <g transform='translate(730 188)'>
        <BreakerBasic
          labels={[{ label: '52-L1', xCoordinate: 40, yCoordinate: 10 }]}
          translateXCoordinate={0}
          translateYCoordinate={0}
          dataPointsWithValues={L1_Breaker_Config.dataPoints}
          defaultHot={true}
        />
      </g>

      {/* vertical line below top breaker */}
      <g transform='translate(742 205)'>
        <VerticalLine
          translateXCoordinate={0}
          translateYCoordinate={11}
          length={40}
          color='white'
          width={0.5}
        />
      </g>

      {/* resistor */}
      <g transform='rotate(90 700 783) translate(170 -35)'>
        <Resistor label={''} />
      </g>

      {/* resistor text */}
      <g transform='translate(770 275)'>
        <text
          x={0}
          y={0}
          fontSize={8}
          fill='white'
          stroke='white'
          strokeWidth={0.2}
        >
          T1
        </text>
      </g>

      {/* vertical line above connector */}
      <g transform='translate(778 300)'>
        <VerticalLine
          translateXCoordinate={-35}
          translateYCoordinate={-17}
          length={62}
          color='white'
          width={0.5}
        />
      </g>

      {/* connector above breaker stacks */}
      <g transform='translate(500 -200)'>
        <MultiSwitchConnector
          translateXCoordinate={-230}
          translateYCoordinate={0}
          horizontalLength={1027}
          verticalLength={20}
          startingXCoordinate={0}
        />
      </g>

      {/* breaker stack 1 */}
      <g transform='translate(380 -357)'>
        <BreakerStack
          breakerConnected={
            F1_Breaker_Config.dataPoints.find(
              (point) =>
                point.pointName ===
                F1_Breaker_Config.points.find(
                  (point) => point.isStatusBinaryPoint,
                )?.pointName,
            )?.value == 1 || false
          }
          deviceDataPoints={F1_Breaker_Config.dataPoints.filter(
            (point) =>
              point.pointName !==
              F1_Breaker_Config.points.find((p) => p.isStatusBinaryPoint)
                ?.pointName,
          )}
          translateXCoordinate={0}
          translateYCoordinate={0}
          feederALabel={''}
          breakerLabel={'52-F1'}
          sectionLabel={'52-F1'}
          deviceDataPointsSectionXCoordinate={0}
          deviceDataPointsSectionYCoordinate={0}
        />

        <text x={-160} y={820} fill='currentColor' stroke='none' fontSize='8px'>
          FEEDER 1A
        </text>

        <HorizontalLine
          translateXCoordinate={-114}
          translateYCoordinate={770}
          length={60}
          color='white'
          width={0.5}
        />

        <VerticalLine
          translateXCoordinate={-53}
          translateYCoordinate={770}
          length={25}
          color='white'
          width={0.5}
        />

        <ArrowDown xCoordinate={-56} yCoordinate={796} />

        <text x={-65} y={820} fill='currentColor' stroke='none' fontSize='8px'>
          FEEDER 1B
        </text>
      </g>

      {/* breaker stack 2 */}
      <g transform='translate(570 -357)'>
        <BreakerStack
          breakerConnected={getBreakerStatus(
            F2_Breaker_Config.dataSourceDeviceEngineeringId,
            F2_Breaker_Config.points.find((point) => point.isStatusBinaryPoint)
              ?.pointName || '',
            F2_Breaker_Config.dataPoints,
          )}
          deviceDataPoints={F2_Breaker_Config.dataPoints.filter(
            (point) =>
              point.pointName !==
              F2_Breaker_Config.points.find((p) => p.isStatusBinaryPoint)
                ?.pointName,
          )}
          translateXCoordinate={0}
          translateYCoordinate={0}
          feederALabel={''}
          breakerLabel={'52-F2'}
          sectionLabel={'52-F2'}
          deviceDataPointsSectionXCoordinate={0}
          deviceDataPointsSectionYCoordinate={0}
        />

        <text x={-160} y={820} fill='currentColor' stroke='none' fontSize='8px'>
          FEEDER 2A
        </text>

        <HorizontalLine
          translateXCoordinate={-114}
          translateYCoordinate={770}
          length={60}
          color='white'
          width={0.5}
        />

        <VerticalLine
          translateXCoordinate={-53}
          translateYCoordinate={770}
          length={25}
          color='white'
          width={0.5}
        />

        <ArrowDown xCoordinate={-56} yCoordinate={796} />

        <text x={-65} y={820} fill='currentColor' stroke='none' fontSize='8px'>
          FEEDER 2B
        </text>
      </g>

      {/* breaker stack 3 */}
      <g transform='translate(770 -357)'>
        <BreakerStack
          breakerConnected={getBreakerStatus(
            F3_Breaker_Config.dataSourceDeviceEngineeringId,
            F3_Breaker_Config.points.find((point) => point.isStatusBinaryPoint)
              ?.pointName || '',
            F3_Breaker_Config.dataPoints,
          )}
          deviceDataPoints={F3_Breaker_Config.dataPoints.filter(
            (point) =>
              point.pointName !==
              F3_Breaker_Config.points.find((p) => p.isStatusBinaryPoint)
                ?.pointName,
          )}
          translateXCoordinate={0}
          translateYCoordinate={0}
          feederALabel={''}
          breakerLabel={'52-F3'}
          sectionLabel={'52-F3'}
          deviceDataPointsSectionXCoordinate={0}
          deviceDataPointsSectionYCoordinate={0}
        />

        <text x={-160} y={820} fill='currentColor' stroke='none' fontSize='8px'>
          FEEDER 3A
        </text>

        <HorizontalLine
          translateXCoordinate={-114}
          translateYCoordinate={770}
          length={60}
          color='white'
          width={0.5}
        />

        <VerticalLine
          translateXCoordinate={-53}
          translateYCoordinate={770}
          length={25}
          color='white'
          width={0.5}
        />

        <ArrowDown xCoordinate={-56} yCoordinate={796} />

        <text x={-65} y={820} fill='currentColor' stroke='none' fontSize='8px'>
          FEEDER 3B
        </text>
      </g>

      {/* breaker stack 4 */}
      <g transform='translate(970 -357)'>
        <BreakerStack
          breakerConnected={getBreakerStatus(
            F4_Breaker_Config.dataSourceDeviceEngineeringId,
            F4_Breaker_Config.points.find((point) => point.isStatusBinaryPoint)
              ?.pointName || '',
            F4_Breaker_Config.dataPoints,
          )}
          deviceDataPoints={F4_Breaker_Config.dataPoints.filter(
            (point) =>
              point.pointName !==
              F4_Breaker_Config.points.find((p) => p.isStatusBinaryPoint)
                ?.pointName,
          )}
          translateXCoordinate={0}
          translateYCoordinate={0}
          feederALabel={''}
          breakerLabel={'52-F4'}
          sectionLabel={'52-F4'}
          deviceDataPointsSectionXCoordinate={0}
          deviceDataPointsSectionYCoordinate={0}
        />

        <text x={-160} y={820} fill='currentColor' stroke='none' fontSize='8px'>
          FEEDER 4A
        </text>

        <HorizontalLine
          translateXCoordinate={-114}
          translateYCoordinate={770}
          length={60}
          color='white'
          width={0.5}
        />

        <VerticalLine
          translateXCoordinate={-53}
          translateYCoordinate={770}
          length={25}
          color='white'
          width={0.5}
        />

        <ArrowDown xCoordinate={-56} yCoordinate={796} />

        <text x={-65} y={820} fill='currentColor' stroke='none' fontSize='8px'>
          FEEDER 4B
        </text>
      </g>

      {/* breaker stack 5 */}
      <g transform='translate(1180 -357)'>
        <BreakerStack
          breakerConnected={getBreakerStatus(
            F5_Breaker_Config.dataSourceDeviceEngineeringId,
            F5_Breaker_Config.points.find((point) => point.isStatusBinaryPoint)
              ?.pointName || '',
            F5_Breaker_Config.dataPoints,
          )}
          deviceDataPoints={F5_Breaker_Config.dataPoints.filter(
            (point) =>
              point.pointName !==
              F5_Breaker_Config.points.find((p) => p.isStatusBinaryPoint)
                ?.pointName,
          )}
          translateXCoordinate={0}
          translateYCoordinate={0}
          feederALabel={''}
          breakerLabel={'52-F5'}
          sectionLabel={'52-F5'}
          deviceDataPointsSectionXCoordinate={0}
          deviceDataPointsSectionYCoordinate={0}
        />

        <text x={-160} y={820} fill='currentColor' stroke='none' fontSize='8px'>
          FEEDER 5A
        </text>

        <HorizontalLine
          translateXCoordinate={-114}
          translateYCoordinate={770}
          length={60}
          color='white'
          width={0.5}
        />

        <VerticalLine
          translateXCoordinate={-53}
          translateYCoordinate={770}
          length={25}
          color='white'
          width={0.5}
        />

        <ArrowDown xCoordinate={-56} yCoordinate={796} />

        <text x={-65} y={820} fill='currentColor' stroke='none' fontSize='8px'>
          FEEDER 5B
        </text>
      </g>

      {/* far right auxiliary  */}
      <g transform='translate(1350 -425)'>
        <VerticalLine
          translateXCoordinate={-53}
          translateYCoordinate={790}
          length={73}
          color='white'
          width={0.5}
        />

        <text x={-85} y={888} fill='currentColor' stroke='none' fontSize='8px'>
          AUXILIARY FEED
        </text>

        <DeviceDataPointsSection
          translateXCoordinate={-115}
          translateYCoordinate={948}
          deviceDataPoints={Auxiliary_Feed_Config.dataPoints}
          label={'AUXILIARY FEED'}
        />

        <ArrowDown xCoordinate={-55.5} yCoordinate={862} />
      </g>
    </svg>
  );
}

export default storeConnector(Evelyn_Diagram, {
  config: ['siteMeta'],
});
